import { StarIcon, ArrowRightIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface TablesSectionProps {
  selectedCourse: string;
  courseTopPerformers: any[];
  courseTopPerformersLoading: boolean;
  recentCourses: any[];
}

const TablesSection = ({
  selectedCourse,
  courseTopPerformers,
  courseTopPerformersLoading,
  recentCourses,
}: TablesSectionProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="grid grid-cols-1 gap-4 md:gap-8 lg:grid-cols-2 px-2 sm:px-0">
      {/* Top Performing Users */}
      <div className="bg-white/5 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200/20 dark:border-gray-700/50 shadow-xl overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200/20 dark:border-gray-700/50">
          <h3 className="text-base sm:text-lg font-semibold text-white">
            {t("analytics.topPerformingUsers")}
          </h3>
          <p className="text-xs sm:text-sm text-gray-300 mt-1">
            {t("analytics.bestPerformingUsers")}
          </p>
        </div>
        <div className="overflow-hidden">
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t("common.user")}
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t("common.department")}
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t("common.score")}
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t("common.completedTutorials")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {selectedCourse && courseTopPerformersLoading ? (
                  <tr>
                    <td colSpan={4} className="px-6 py-12">
                      <div className="flex items-center justify-center">
                        <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                        <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                          {t("analytics.loadingUsers")}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : selectedCourse &&
                  courseTopPerformers &&
                  courseTopPerformers.length > 0 ? (
                  courseTopPerformers.slice(0, 3).map(
                    (user: any, index: number) => (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-150"
                      >
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                              <span className="text-xs sm:text-sm font-medium text-primary-700 dark:text-primary-300">
                                {user.name?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate max-w-[100px] sm:max-w-none">
                              {user.name}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                          {user.department}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                          {user.score}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                          {user.completed}
                        </td>
                      </tr>
                    )
                  )
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-12">
                      <div className="text-center">
                        <svg
                          className="w-12 h-12 text-gray-400 mx-auto mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">
                          {t("analytics.noUsersFound")}
                        </p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                          {t(
                            "analytics.noRegisteredUser",
                            "No users have taken this course yet."
                          )}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recent Courses */}
      <div className="bg-white/5 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-200/20 dark:border-gray-700/50 shadow-xl overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200/20 dark:border-gray-700/50">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-semibold text-white">
                {t("analytics.recentCourses")}
              </h3>
              <p className="text-xs sm:text-sm text-gray-300 mt-1">
                {t("analytics.latestCoursesAdded")}
              </p>
            </div>
            <button
              onClick={() => navigate('/courses')}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors duration-200 whitespace-nowrap flex-shrink-0"
            >
{t("analytics.viewAll")}
              <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
        <div className="overflow-hidden">
          <div className="overflow-x-auto -mx-2 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t("analytics.course")}
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t("analytics.enrollments")}
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t("analytics.completion")}
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                    {t("analytics.rating")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {recentCourses.length > 0 ? (
                  recentCourses.map((course: any, index: number) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-150"
                    >
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white max-w-[150px] sm:max-w-none truncate">
                          {course.title}
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                        {course.enrollments.toLocaleString()}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                        <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          {course.completion}%
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                        <div className="flex items-center justify-end">
                          <span className="mr-1 font-medium">
                            {course.rating}
                          </span>
                          <StarIcon className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400" />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="px-6 py-12">
                      <div className="text-center">
                        <svg
                          className="w-12 h-12 text-gray-400 mx-auto mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">
                          {t("analytics.noCoursesAvailable")}
                        </p>
                        <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                          {t("analytics.noCoursesAddedYet")}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TablesSection;
